// ФАЙЛ: DashboardPage.jsx

import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
    CContainer, CRow, CCol, CCard, CCardBody, CCardHeader, CButton, CSpinner,
    CButtonGroup, CFormCheck, CModal, CModalHeader, CModalTitle, CModalBody,
    CModalFooter, CFormInput, CAlert, CFormSwitch
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilBell } from '@coreui/icons';
import { cilAccountLogout, cilUser, cilVideo, cilClone, cilTrash, cilLink } from '@coreui/icons';
import { toast } from 'react-toastify';
import Loading from '../../components/Loading/Loading';
import MultiCameraAnalytics from '../../components/MultiCameraAnalytics/MultiCameraAnalytics';
import './DashboardPage.css';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../../components/LanguageSwitcher/LanguageSwitcher';

//ПРАВА
import { getUserRoles } from '../../utils/authUtils';

const DashboardPage = () => {
    const { t } = useTranslation();
    const [cameras, setCameras] = useState([]);
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [selectedCameraIds, setSelectedCameraIds] = useState([]);
    const [showHint, setShowHint] = useState(false);

    // Получаем права
    const { is_staff, is_superuser } = getUserRoles();
    const hasAdminRights = is_staff || is_superuser;

    // --- ИЗМЕНЕНИЕ 1: Состояние модалки теперь хранит массив выбранных камер ---
    const [combineModal, setCombineModal] = useState({ visible: false, selectedCams: [], name: '' });
    const fetchInitialData = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const [camRes, userRes] = await Promise.all([
                axios.get('/api/cameras/', { headers: { 'Authorization': `Bearer ${token}` } }),
                axios.get('/api/profile/', { headers: { 'Authorization': `Bearer ${token}` } })
            ]);
            setCameras(camRes.data.results);
            setUser(userRes.data);
            localStorage.setItem('keepDates', userRes.data.organization?.keep_dates_between_posts ? 'true' : 'false');
        } catch (err) {
            toast.error(t('common.error'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInitialData();
    }, [t]);

    useEffect(() => {
        if (cameras.filter(c => !c.parent && !c.is_combined_camera).length > 1 && selectedCameraIds.length === 0) {
            const timer = setTimeout(() => setShowHint(true), 1500);
            return () => clearTimeout(timer);
        } else {
            setShowHint(false);
        }
    }, [cameras, selectedCameraIds]);

    const handleCameraSelect = (cameraId) => {
        setSelectedCameraIds(prev =>
            prev.includes(cameraId)
                ? prev.filter(id => id !== cameraId)
                : [...prev, cameraId]
        );
    };

    const handleDuplicate = async (cameraId) => {
        if (!window.confirm(t('dashboard.confirm_duplicate'))) return;
        try {
            const token = localStorage.getItem('token');
            await axios.post(`/api/cameras/${cameraId}/duplicate/`, {}, { headers: { 'Authorization': `Bearer ${token}` } });
            toast.success(t('common.success'));
            fetchInitialData();
        } catch (err) {
            toast.error(err.response?.data?.error || t('common.error'));
        }
    };

    const handleDelete = async (postId, postName) => {
        if (!window.confirm(`${t('dashboard.btn_delete')} "${postName}"?`)) return;
        setDeletingId(postId);
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`/api/cameras/${postId}/`, { headers: { 'Authorization': `Bearer ${token}` } });
            toast.success(t('common.success'));
            setSelectedCameraIds(prev => prev.filter(id => id !== postId));
            await fetchInitialData();
        } catch (error) {
            toast.error(error.response?.data?.error || t('common.error'));
        } finally {
            setDeletingId(null);
        }
    };

    // --- ИЗМЕНЕНИЕ 2: Открытие модалки для любого количества камер (>= 2) ---
    const openCombineModal = () => {
        // Фильтруем: берем только те выбранные камеры, которые являются основными
        const selectedMasterCams = cameras.filter(c => selectedCameraIds.includes(c.id) && !c.parent && !c.is_combined_camera);

        // Кнопка должна показываться, только если >= 2, но перестрахуемся
        if (selectedMasterCams.length < 2) return;

        // Формируем имя из первых трех камер (чтобы не было слишком длинным)
        let generatedName = "Совмещенный: " + selectedMasterCams.slice(0, 3).map(c => c.name).join(' + ');
        if (selectedMasterCams.length > 3) {
            generatedName += ` и еще ${selectedMasterCams.length - 3}...`;
        }

        setCombineModal({
            visible: true,
            selectedCams: selectedMasterCams,
            name: generatedName
        });
    };

    const closeCombineModal = () => setCombineModal({ visible: false, selectedCams: [], name: '' });

    // --- ИЗМЕНЕНИЕ 3: Отправка массива ID ---
    const handleCombine = async () => {
        if (!combineModal.name.trim()) {
            toast.warn('Название не может быть пустым');
            return;
        }
        try {
            const token = localStorage.getItem('token');
            const payload = {
                camera_ids: combineModal.selectedCams.map(c => c.id), // Отправляем все ID
                name: combineModal.name
            };
            await axios.post('/api/cameras/combine/', payload, { headers: { 'Authorization': `Bearer ${token}` } });
            toast.success('Совмещенный пост успешно создан!');
            closeCombineModal();
            setSelectedCameraIds([]);
            fetchInitialData();
        } catch (err) {
            toast.error(err.response?.data?.error || t('common.error'));
        }
    };

    const handleLogout = () => { localStorage.removeItem('token'); window.location.href = '/login'; };

    if (loading) return <Loading name={t('common.loading')} />;

    const selectedCamerasData = cameras.filter(cam => selectedCameraIds.includes(cam.id));

    // --- ИЗМЕНЕНИЕ 4: Для кнопки "Объединить" считаем только основные камеры (не дубли, не совмещенные) ---
    const masterCamerasSelected = selectedCamerasData.filter(c => !c.parent && !c.is_combined_camera);


    // --- ОБРАБОТЧИК СОХРАНЕНИЯ ДАТ ---
    const handleToggleKeepDates = async (e) => {
        const isChecked = e.target.checked;
        try {
            const token = localStorage.getItem('token');
            await axios.patch('/api/organization/',
                { keep_dates_between_posts: isChecked },
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            setUser(prev => ({
                ...prev,
                organization: { ...prev.organization, keep_dates_between_posts: isChecked }
            }));
            localStorage.setItem('keepDates', isChecked ? 'true' : 'false');
            toast.success("Настройки сохранены");
        } catch (err) {
            toast.error("Ошибка сохранения настроек");
        }
    };

    return (
        <div className="bg-light min-vh-100 d-flex flex-row">
            <CContainer>
                <CRow className="justify-content-center">
                    <CCol md={10}>
                        <div className="d-flex justify-content-between align-items-center mb-4 mt-4">
                            <div>
                                <h2>{t('dashboard.title')}</h2>
                                {user && <p className="text-medium-emphasis mb-0">{t('dashboard.org')}: {user.organization?.name || '---'}</p>}
                            </div>
                            <div className="d-flex align-items-center">
                                <CButton color="warning" variant="outline" as={Link} to="/notifications" className="me-2">
                                    <CIcon icon={cilBell} className="me-2" /> {t('dashboard.notifications')}
                                </CButton>
                                {hasAdminRights && masterCamerasSelected.length >= 2 && (
                                    <CButton color="success" className="text-white me-2" onClick={openCombineModal}>
                                        <CIcon icon={cilLink} className="me-2" /> {t('dashboard.combine_btn')} ({masterCamerasSelected.length})
                                    </CButton>
                                )}

                                {user?.is_staff && (
                                    <CButton color="info" as={Link} to="/users" className="text-white me-2">
                                        <CIcon icon={cilUser} className="me-2" /> {t('dashboard.manage_users')}
                                    </CButton>
                                )}
                                <CButton color="secondary" onClick={handleLogout}><CIcon icon={cilAccountLogout} className="me-2" />{t('dashboard.logout')}</CButton>
                                <LanguageSwitcher />
                            </div>
                        </div>
                        {user?.organization && (
                            <div className="mb-3 ms-1">
                                <CFormSwitch
                                    id="keepDatesSwitch"
                                    label="Сохранять выбранные даты при переходе между постами"
                                    checked={user.organization.keep_dates_between_posts}
                                    onChange={handleToggleKeepDates}
                                    className="fw-bold text-primary"
                                />
                            </div>
                        )}
                        {cameras.length === 0 ? (
                            <CCard><CCardBody>{t('dashboard.no_posts')}</CCardBody></CCard>
                        ) : (
                            <CRow>
                                {cameras.map((cam, index) => (
                                    <CCol md={6} key={cam.id} className="mb-3">
                                        <div className="hint-wrapper">
                                            {index === 0 && showHint && (
                                                <div className="animated-hint">
                                                    {t('dashboard.hint_select')}
                                                    <svg width="50px" height="50px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18 18L6 6" stroke="#321fdb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /><path d="M8 18L18 18L18 8" stroke="#321fdb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                                </div>
                                            )}
                                            <CCard className="h-100 shadow-sm">
                                                <CCardHeader className="d-flex justify-content-between align-items-center">
                                                    <strong><CIcon icon={cilVideo} className="me-2" />{cam.name}</strong>
                                                    <CFormCheck id={`cam-check-${cam.id}`} checked={selectedCameraIds.includes(cam.id)} onChange={() => handleCameraSelect(cam.id)} />
                                                </CCardHeader>
                                                <CCardBody className="d-flex flex-column">
                                                    <div className="mb-1 text-muted small flex-grow-1" style={{ wordBreak: 'break-all' }}>
                                                        {/* ИЗМЕНЕНИЕ 6: Правильное отображение информации о дублях и совмещенных */}
                                                        {cam.is_duplicate && `(Дубль поста #${cam.parent})`}
                                                        {cam.is_combined && `(Совмещенный: #${cam.parent} + ${cam.additional_cameras?.length || 0} доп. камер)`}
                                                        {!cam.parent && !cam.is_combined_camera && cam.rtsp_url}
                                                    </div>
                                                    <CButtonGroup className="mt-2 w-100">
                                                        <Link to={`/camera/${cam.id}`} className="text-decoration-none w-100">
                                                            <CButton color="primary" variant="outline" className="w-100 rounded-end-0">{t('dashboard.btn_analytics')}</CButton>
                                                        </Link>
                                                        {/* ИЗМЕНЕНИЕ: Дублировать можно только основные посты и только админам */}
                                                        {hasAdminRights && !cam.parent && !cam.is_combined_camera && (
                                                            <CButton color="secondary" variant="outline" onClick={() => handleDuplicate(cam.id)} title={t('dashboard.btn_duplicate')}><CIcon icon={cilClone} /></CButton>
                                                        )}

                                                        {/* ИЗМЕНЕНИЕ: Удалять можно дубли и совмещенные и только админам */}
                                                        {hasAdminRights && (cam.parent || cam.is_combined_camera) && (
                                                            <CButton color="danger" variant="outline" onClick={() => handleDelete(cam.id, cam.name)} title={t('dashboard.btn_delete')} disabled={deletingId === cam.id} className="rounded-start-0">{deletingId === cam.id ? <CSpinner size="sm" /> : <CIcon icon={cilTrash} />}</CButton>
                                                        )}
                                                    </CButtonGroup>
                                                </CCardBody>
                                            </CCard>
                                        </div>
                                    </CCol>
                                ))}
                            </CRow>
                        )}
                    </CCol>

                    {selectedCameraIds.length > 0 && (<MultiCameraAnalytics selectedCameras={selectedCamerasData} />)}
                </CRow>

                <CModal alignment="center" visible={combineModal.visible} onClose={closeCombineModal}>
                    <CModalHeader><CModalTitle>{t('dashboard.combine_modal_title')}</CModalTitle></CModalHeader>
                    <CModalBody>
                        <p>{t('dashboard.combine_modal_body_1')} {combineModal.selectedCams.length} камер:</p>
                        <ul>
                            {combineModal.selectedCams.map(cam => (
                                <li key={cam.id}><strong>{cam.name}</strong></li>
                            ))}
                        </ul>
                        <CAlert color="info">{t('dashboard.combine_modal_alert')}</CAlert>
                        <CFormInput
                            label={t('dashboard.combine_name_label')}
                            value={combineModal.name}
                            onChange={(e) => setCombineModal(prev => ({ ...prev, name: e.target.value }))}
                        />
                    </CModalBody>
                    <CModalFooter>
                        <CButton color="secondary" onClick={closeCombineModal}>{t('common.cancel')}</CButton>
                        <CButton color="primary" onClick={handleCombine}>{t('dashboard.combine_create_btn')}</CButton>
                    </CModalFooter>
                </CModal>
            </CContainer>
        </div>
    );
};
export default DashboardPage;